import {isNotNil, sortBy} from 'ramda';

import type {
  ColourResponseBody,
  EngineResponseBody,
  PackageResponseBody,
  TrimResponseBody,
  VariantResponseBody,
} from '@dms/api/new-vehicle';

import type {PricedItemChange} from '../types/PricedItemChange';
import {createdItem} from './createdItem';
import {isInPricelist} from './isInPricelist';
import {removedItem} from './removedItem';
import {trimFeatureChanges} from './trimFeatureChanges';
import {variantChangeName} from './variantChangeName';

type TrimFeatureChangesOptions = Parameters<typeof trimFeatureChanges>[0];

type TrimPublicationChangesOptions = {
  /** The trim with the base price the write sends (draft → online may change it in the same PATCH). */
  trim: Pick<TrimResponseBody, 'id' | 'name' | 'basePriceNet'>;
  /** publish: draft → online; unpublish: online → draft. */
  direction: 'publish' | 'unpublish';
  /** The Variants of the model year (the matrix cells); the trim's own are picked here. */
  variants: Pick<
    VariantResponseBody,
    'id' | 'trimId' | 'engineId' | 'orderCode' | 'priceNet' | 'enabled' | 'archived'
  >[];
  engines: Pick<EngineResponseBody, 'id' | 'name' | 'archived'>[];
  /** The trim's feature assignments as the server holds them (a fresh GET). */
  assignments: TrimFeatureChangesOptions['stored'];
  /** Library items, for their names. */
  features: TrimFeatureChangesOptions['features'];
  /** The colours and packages of the model year; their rows on this trim are picked here. */
  colours: Pick<ColourResponseBody, 'id' | 'name' | 'archived' | 'trims'>[];
  packages: Pick<PackageResponseBody, 'id' | 'name' | 'status' | 'trims'>[];
};

/**
 * The rows of a trim status switch on a published model year: every price the switch puts into the dealer
 * pricelist (publish, — → price) or takes out of it (unpublish, price → —). These are the base price, the price of
 * each enabled, non-archived Variant of the trim whose engine is not archived (a disabled Variant is not in the
 * pricelist), each live trim feature price at its charged price (Standard = 0.00), and the trim's rows of
 * colours not archived and of active packages (D102), as the server's TrimPricelistRows. Rows are ordered
 * trim, Variants by id, feature prices, colours, then packages.
 */
export const trimPublicationChanges = (
  options: TrimPublicationChangesOptions
): PricedItemChange[] => {
  const isPublish = options.direction === 'publish';
  const item = isPublish ? createdItem : removedItem;
  const engineOf = (engineId: string) => options.engines.find(({id}) => id === engineId);
  const trimVariants = options.variants.filter(({trimId}) => trimId === options.trim.id);
  const nameOf = (variant: (typeof trimVariants)[number]) =>
    variantChangeName(
      options.trim.name,
      engineOf(variant.engineId)?.name ?? variant.engineId,
      variant.orderCode
    );
  const variantRows = sortBy(
    ({id}) => id,
    trimVariants.filter((variant) => {
      const engine = engineOf(variant.engineId);
      return variant.enabled && !variant.archived && isNotNil(engine) && !engine.archived;
    })
  ).map((variant) => item('variant', variant.id, nameOf(variant), variant.priceNet));
  const variantOf = (variantId: string) => trimVariants.find(({id}) => id === variantId);
  const featureRows = trimFeatureChanges({
    trimId: options.trim.id,
    trimName: options.trim.name,
    stored: isPublish ? [] : options.assignments,
    next: isPublish ? options.assignments : [],
    features: options.features,
    variantName: (variantId) => {
      const variant = variantOf(variantId);
      return isNotNil(variant) ? nameOf(variant) : variantId;
    },
    variantOrderCode: (variantId) => variantOf(variantId)?.orderCode ?? variantId,
  });
  const offeredRows = (
    resourceType: 'colourTrim' | 'packageTrim',
    items: {id: string; name: string; trims: {trimId: string; priceNet: string}[]}[]
  ) =>
    items.flatMap((offered) =>
      offered.trims
        .filter(({trimId}) => trimId === options.trim.id)
        .map(({priceNet}) =>
          item(
            resourceType,
            `${resourceType}:${offered.id}`,
            `${options.trim.name} · ${offered.name}`,
            priceNet
          )
        )
    );
  return [
    item('trim', options.trim.id, options.trim.name, options.trim.basePriceNet),
    ...variantRows,
    ...featureRows,
    ...offeredRows(
      'colourTrim',
      options.colours.filter(({archived}) => !archived)
    ),
    ...offeredRows(
      'packageTrim',
      options.packages.filter(({status}) => isInPricelist.package(status))
    ),
  ];
};
